/**
 * People are named as a first and a last name, but the identity store keeps one
 * `displayName` string (better-auth owns that table and its single `name`
 * column). These two functions are the only place that correspondence is
 * defined.
 *
 * The split is first-token/remainder rather than last-token/remainder so that
 * multi-part family names ("Ada Lovelace King") stay whole, which is the more
 * common case in the names this product carries. The pairing round-trips: any
 * `displayName` split and rejoined yields the original string, so opening the
 * form and saving it unchanged is never a silent edit.
 */

export interface PersonName {
  firstName: string;
  lastName: string;
}

export function splitPersonName(displayName: string | null | undefined): PersonName {
  const trimmed = (displayName ?? "").trim().replace(/\s+/g, " ");
  if (!trimmed) return { firstName: "", lastName: "" };
  const boundary = trimmed.indexOf(" ");
  if (boundary === -1) return { firstName: trimmed, lastName: "" };
  return {
    firstName: trimmed.slice(0, boundary),
    lastName: trimmed.slice(boundary + 1),
  };
}

/**
 * Joins to the stored `displayName`. Last name is optional: mononymous people
 * exist, and a required surname would lock them out of their own profile.
 */
export function joinPersonName(name: PersonName): string {
  return [name.firstName.trim(), name.lastName.trim()].filter(Boolean).join(" ");
}
